
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import IndoorMap from '@/components/maps/IndoorMap';
import OutdoorMap from '@/components/maps/OutdoorMap';
import { useTourDetail } from '@/hooks/useTourDetail';

const TourMap = () => {
  const { id } = useParams();
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  // Use our hook to fetch tour data
  const { tour, tourExhibits, loading, error } = useTourDetail(id);
  
  // If tour not found or loading, handle it
  if (loading) {
    return (
      <div className="flex flex-col h-screen">
        <div className="bg-white py-3 px-4 shadow-sm flex items-center">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/tours/${id}`)}>
            <ArrowLeft />
          </Button>
          <h1 className="ml-2 text-lg font-medium">
            {language === 'en' ? 'Loading Tour Map...' : 'טוען מפת סיור...'}
          </h1>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-museum mx-auto mb-4"></div>
            <p>{language === 'en' ? 'Loading...' : 'טוען...'}</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (error || !tour) {
    return (
      <div className="flex flex-col h-screen">
        <div className="bg-white py-3 px-4 shadow-sm flex items-center">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/tours/${id}`)}>
            <ArrowLeft />
          </Button>
          <h1 className="ml-2 text-lg font-medium">
            {language === 'en' ? 'Tour Map' : 'מפת סיור'}
          </h1>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 mb-4">
              {language === 'en' ? 'Tour not found' : 'הסיור לא נמצא'}
            </p>
            <Button onClick={() => navigate('/tours')}>
              {language === 'en' ? 'Back to Tours' : 'חזור לסיורים'}
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // Convert TourDetailExhibit to the format expected by maps
  const mapExhibits = tourExhibits.map(exhibit => ({
    id: parseInt(exhibit.id) || 0, // Convert string ID to number for map compatibility
    numberCode: exhibit.id, // Use ID as number code
    titleEn: exhibit.titleEn,
    titleHe: exhibit.titleHe,
    descriptionEn: exhibit.titleEn,
    descriptionHe: exhibit.titleHe,
    image: exhibit.image,
    hasAudio: false,
    hasVideo: false,
    location: {
      floor: exhibit.location?.floor || 1,
      room: exhibit.location?.room || 'Room 1',
      x: 100, // Default position for indoor maps
      y: 100,
      latitude: undefined,
      longitude: undefined
    }
  }));
  
  // Count exhibits with valid coordinates for outdoor tours
  const validExhibitsCount = tour.mapType === 'outdoor' ? 
    mapExhibits.filter(exhibit => exhibit.location.latitude && exhibit.location.longitude).length : 
    mapExhibits.length;

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <div className="bg-white py-3 px-4 shadow-sm flex items-center justify-between">
        <div className="flex items-center">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/tours/${id}`)}>
            <ArrowLeft />
          </Button>
          <h1 className="ml-2 text-lg font-medium">
            {language === 'en' ? 'Tour Map' : 'מפת הסיור'}: {tour.title}
          </h1>
        </div>
        
        {tour.mapType === 'outdoor' && (
          <div className="text-sm font-medium text-museum">
            {validExhibitsCount} {language === 'en' ? 'locations' : 'מיקומים'}
          </div>
        )}
      </div>
      
      {/* Map Container */}
      <div className="flex-1 relative">
        {tour.mapType === 'indoor' && tour.mapImage ? (
          <IndoorMap mapImage={tour.mapImage} exhibits={mapExhibits} />
        ) : tour.mapType === 'outdoor' ? (
          <OutdoorMap exhibits={mapExhibits} />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-gray-500 mb-4">
                {language === 'en' ? 'No map available for this tour' : 'אין מפה זמינה לסיור זה'}
              </p>
              <Button onClick={() => navigate(`/tours/${id}`)}>
                {language === 'en' ? 'Back to Tour' : 'חזור לסיור'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TourMap;
